import type { JSONValue } from './Assertions.ts'

type JSONRecord = Record<string, unknown>

export interface JsonRpcMcpClientOptions {
  post?: (body: JSONRecord) => Promise<JSONRecord>
  url?: string
  headers?: HeadersInit | (() => HeadersInit | Promise<HeadersInit>)
  fetch?: typeof fetch
}

export class JsonRpcMcpClient {
  #post: (body: JSONRecord) => Promise<JSONRecord>
  #nextId = 1

  constructor(options: JsonRpcMcpClientOptions) {
    if (options.post) {
      this.#post = options.post
      return
    }

    if (!options.url) {
      throw new Error('JsonRpcMcpClient requires post or url')
    }

    const fetchFn = options.fetch ?? fetch
    this.#post = async (body) => {
      const headers = typeof options.headers === 'function'
        ? await options.headers()
        : options.headers ?? {}
      const response = await fetchFn(options.url!, {
        method: 'POST',
        headers: jsonHeaders(headers),
        body: JSON.stringify(body),
      })
      return await response.json()
    }
  }

  async listTools(): Promise<JSONValue[]> {
    const result = await this.#request('tools/list', {})
    const tools = (result as JSONRecord | undefined)?.tools
    return Array.isArray(tools) ? tools as JSONValue[] : []
  }

  async callTool(
    name: string,
    args: JSONRecord = {},
  ): Promise<JSONValue> {
    const result = await this.#request('tools/call', {
      name,
      arguments: args,
    })

    if ((result as JSONRecord | undefined)?.isError) {
      throw mcpToolErrorMessage(result)
    }

    const record = result as JSONRecord | null | undefined
    return record?.structuredContent as JSONValue ?? null
  }

  async #request(method: string, params: JSONRecord): Promise<JSONValue> {
    const json = await this.#post({
      jsonrpc: '2.0',
      id: this.#nextId++,
      method,
      params,
    })

    if (json.error) {
      const error = json.error as { message?: string }
      throw error.message ?? JSON.stringify(json.error)
    }

    return json.result as JSONValue
  }
}

export function mcpToolErrorMessage(result: unknown): string {
  const content = (result as JSONRecord | undefined)?.content
  if (Array.isArray(content)) {
    const first = content[0] as { text?: unknown } | undefined
    if (typeof first?.text === 'string') return first.text
  }
  return 'MCP tool failed'
}

function jsonHeaders(headers: HeadersInit): HeadersInit {
  const out = new Headers(headers)
  out.set('Content-Type', 'application/json')
  return out
}
